import React, { useState } from "react";
import "./style.scss";

const Terms = () => {
    return (
        <>
            <h3>Terms and Conditions</h3>

            <section>
                <h4>1. Introduction</h4>
                <p>
                    Welcome to <strong>Jamlyf</strong>. These Terms and Conditions govern
                    your access to and use of the Jamlyf platform. By accessing or using
                    Jamlyf, you agree to be bound by these Terms. If you do not agree, you
                    must not use the platform.
                </p>
            </section>

            <section>
                <h4>2. Definitions</h4>
                <ul>
                    <li><strong>Jamlyf</strong> – the Jamlyf platform and services.</li>
                    <li><strong>Spot</strong> – a club, lounge, bar, or event venue using Jamlyf.</li>
                    <li><strong>Raver</strong> – a customer placing an order via Jamlyf.</li>
                    <li><strong>Steward</strong> – staff managing orders on behalf of a spot.</li>
                    <li><strong>User</strong> – any person using the platform.</li>
                </ul>
            </section>

            <section>
                <h4>3. Description of Service</h4>
                <p>
                    Jamlyf provides a QR-based digital ordering system that allows ravers to
                    view menus and place orders, while spots and stewards manage those
                    orders in real time. Jamlyf does not own, operate, or manage venues and
                    does not fulfill orders.
                </p>
            </section>

            <section>
                <h4>4. Eligibility</h4>
                <p>
                    You must be at least 18 years old to use Jamlyf. By using the platform,
                    you confirm that you meet this requirement.
                </p>
            </section>

            <section>
                <h4>5. Accounts and Responsibilities</h4>
                <ul>
                    <li>Spots must provide accurate and up-to-date information.</li>
                    <li>Account credentials must be kept secure.</li>
                    <li>Spots are responsible for all activity under their account.</li>
                </ul>
                <p>
                    Jamlyf is not responsible for unauthorized access resulting from failure
                    to secure account credentials.
                </p>
            </section>

            <section>
                <h4>6. Orders and Fulfillment</h4>
                <p>
                    All orders placed through Jamlyf are fulfilled by the spot. Jamlyf does
                    not guarantee:
                </p>
                <ul>
                    <li>Order availability</li>
                    <li>Order accuracy</li>
                    <li>Delivery time</li>
                    <li>Service quality</li>
                </ul>
            </section>

            <section>
                <h4>7. Payments</h4>
                <p>
                    Jamlyf may integrate third-party payment providers. Jamlyf does not store
                    sensitive payment information and is not responsible for payment
                    failures or disputes caused by third-party providers.
                </p>
            </section>

            <section>
                <h4>8. Acceptable Use</h4>
                <p>Users must not:</p>
                <ul>
                    <li>Use Jamlyf for unlawful purposes</li>
                    <li>Attempt unauthorized system access</li>
                    <li>Disrupt platform performance</li>
                    <li>Misuse QR codes or impersonate others</li>
                </ul>
            </section>

            <section>
                <h4>9. Intellectual Property</h4>
                <p>
                    All Jamlyf software, branding, and content are the property of Jamlyf.
                    Users may not copy, modify, or distribute any part of the platform
                    without permission.
                </p>
            </section>

            <section>
                <h4>10. Limitation of Liability</h4>
                <p>
                    Jamlyf is provided on an “as is” basis. Jamlyf shall not be liable for
                    indirect, incidental, or consequential damages arising from use of the
                    platform.
                </p>
            </section>

            <section>
                <h4>11. Termination</h4>
                <p>
                    Jamlyf reserves the right to suspend or terminate access to the platform
                    at any time if these Terms are violated.
                </p>
            </section>

            <section>
                <h4>12. Changes to Terms</h4>
                <p>
                    Jamlyf may update these Terms periodically. Continued use of the
                    platform constitutes acceptance of the updated Terms.
                </p>
            </section>

            <section>
                <h4>13. Governing Law</h4>
                <p>
                    These Terms are governed by the laws applicable in the jurisdictions
                    where Jamlyf operates.
                </p>
            </section>
        </>
    );
}

const Privacy = () => {
    return (
        <>
            <h3>Privacy Policy</h3>

            <section>
                <h4>1. Introduction</h4>
                <p>
                    Jamlyf respects your privacy and is committed to protecting your
                    personal data. This policy explains how we collect, use, and safeguard
                    information.
                </p>
            </section>

            <section>
                <h4>2. Information We Collect</h4>
                <ul>
                    <li>Contact details (e.g. name, email)</li>
                    <li>Order and usage data</li>
                    <li>Spot and steward account details</li>
                    <li>Technical data such as IP address and browser type</li>
                </ul>
            </section>

            <section>
                <h4>3. How We Use Information</h4>
                <ul>
                    <li>Operate and maintain the Jamlyf platform</li>
                    <li>Process and manage orders</li>
                    <li>Improve platform performance</li>
                    <li>Provide customer support</li>
                    <li>Ensure security and prevent misuse</li>
                </ul>
            </section>

            <section>
                <h4>4. Data Sharing</h4>
                <p>
                    Jamlyf does not sell personal data. Information may be shared with:
                </p>
                <ul>
                    <li>Spots fulfilling orders</li>
                    <li>Payment providers</li>
                    <li>Service providers required to operate the platform</li>
                </ul>
            </section>

            <section>
                <h4>5. Payments</h4>
                <p>
                    Jamlyf does not store payment card information. All payments are
                    processed by secure third-party providers.
                </p>
            </section>

            <section>
                <h4>6. Data Security</h4>
                <p>
                    We apply reasonable technical and organizational measures to protect
                    data. However, no system can be guaranteed to be completely secure.
                </p>
            </section>

            <section>
                <h4>7. Account Deletion and Data Retention</h4>
                <p>
                    Users may delete their accounts at any time through the app settings or by contacting Jamlyf support. Upon account deletion:
                </p>
                <ul>
                    <li>Your account will be immediately deactivated and inaccessible</li>
                    <li>Personal data will be retained for 90 days to allow for account recovery, resolve disputes, comply with legal obligations, and complete any pending transactions</li>
                    <li>After 90 days, all personal data associated with your account will be permanently deleted from our systems, except where retention is required by law</li>
                </ul>
                <p>
                    Data related to active accounts is retained only for as long as necessary to operate Jamlyf and comply with legal obligations.
                </p>
            </section>

            <section>
                <h4>8. Cookies</h4>
                <p>
                    Jamlyf may use cookies and similar technologies to enhance user
                    experience and analyze platform usage.
                </p>
            </section>

            <section>
                <h4>9. User Rights</h4>
                <p>
                    Users may request access, correction, or deletion of personal data,
                    subject to applicable laws.
                </p>
            </section>

            <section>
                <h4>10. Changes to This Policy</h4>
                <p>
                    This Privacy Policy may be updated from time to time. Continued use of
                    Jamlyf indicates acceptance of the updated policy.
                </p>
            </section>

            <section>
                <h4>11. Contact</h4>
                <p>
                    If you have questions about this Privacy Policy, please contact Jamlyf
                    through the website.
                </p>
            </section>
        </>
    );
}

export default function LegalPage() {
    const [activeTab, setActiveTab] = useState("terms");

    return (
        <div className="legal-page">
            <div className="tabs">
                <button
                    className={activeTab === "terms" ? "active" : ""}
                    onClick={() => setActiveTab("terms")}
                >
                    Terms & Conditions
                </button>

                <button
                    className={activeTab === "privacy" ? "active" : ""}
                    onClick={() => setActiveTab("privacy")}
                >
                    Privacy Policy
                </button>
            </div>

            <div className="tab-content">
                {activeTab === "terms" ? <Terms /> : <Privacy />}
            </div>
            <p className="meta">Version 1.0 · Last updated: February 2026</p>
        </div>
    );
}
